import { useEffect, useState } from "react";
import { FaSyncAlt, FaSearch } from "react-icons/fa";
import { apiFetch } from "../../utils/api";
import { showSuccess, showError } from "../../utils/dialog";
import RichAuditsPanel from "../RichAuditsPanel";
import DateInput from "../DateInput";

const fetchJson = async (url, opts) => {
  try {
    const res = await apiFetch(url, opts);
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`${res.status} ${res.statusText} - ${t}`);
    }
    return await res.json();
  } catch (e) {
    if (e.name === "AbortError") return null;
    console.error("EditAdjustmentModal fetch error", e);
    return null;
  }
};

const EditAdjustmentModal = ({
  isOpen,
  onClose,
  adjustment,
  onSaved,
  isWeekClosed,
}) => {
  const [tab, setTab] = useState("General");
  const [form, setForm] = useState({
    subject_name: "",
    subject_type: "",
    recognition_date: "",
    description: "",
    note: "",
    quantity: 1,
    amount: 0,
    add_hst: "no",
    client_id: null,
    account_id: null,
  });
  const [weekClosed, setWeekClosed] = useState(Boolean(isWeekClosed));
  const [subjectQuery, setSubjectQuery] = useState("");
  const [subjectSuggestions, setSubjectSuggestions] = useState([]);
  const [showSubjectSuggestions, setShowSubjectSuggestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [forceSave, setForceSave] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      subject_name: adjustment?.subject_name ?? "",
      subject_type: adjustment?.subject_type ?? "",
      recognition_date: adjustment?.recognition_date
        ? adjustment.recognition_date.slice(0, 10)
        : "",
      description: adjustment?.description ?? "",
      note: adjustment?.note ?? "",
      quantity:
        typeof adjustment?.quantity !== "undefined" ? adjustment.quantity : 1,
      amount: typeof adjustment?.amount !== "undefined" ? adjustment.amount : 0,
      add_hst: adjustment?.add_hst ? "yes" : "no",
      client_id: adjustment?.client_id ?? null,
      account_id: adjustment?.account_id ?? null,
    });
    setSubjectQuery(
      adjustment?.subject_type
        ? `${adjustment.subject_type}: ${adjustment.subject_name}`
        : adjustment?.subject_name || "",
    );

    // Determine week closed state for the adjustment's recognition date
    (async () => {
      try {
        const date = adjustment?.recognition_date
          ? adjustment.recognition_date.slice(0, 10)
          : null;
        if (!date) {
          setWeekClosed(Boolean(isWeekClosed));
          return;
        }
        const res = await apiFetch(
          `/api/accounting/weeks?startDate=${encodeURIComponent(date)}`,
        );
        if (!res.ok) {
          setWeekClosed(Boolean(isWeekClosed));
          return;
        }
        const j = await res.json().catch(() => null);
        const wk = j?.data || null;
        setWeekClosed(String(wk?.state || "").toLowerCase() === "closed");
      } catch (e) {
        console.warn("Failed to fetch week state", e);
        setWeekClosed(Boolean(isWeekClosed));
      }
    })();
  }, [isOpen, adjustment]);

  const handleChange = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Subject autocomplete: search both clients and staff
  useEffect(() => {
    if (!subjectQuery || subjectQuery.trim().length === 0) {
      setSubjectSuggestions([]);
      setShowSubjectSuggestions(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // staff
        const staffUrl = `/api/personnel/accounts/search?q=${encodeURIComponent(subjectQuery)}&limit=6`;
        const csUrl = `/api/accounting/clients?search=${encodeURIComponent(subjectQuery)}&limit=6`;
        const [sRes, cRes] = await Promise.all([
          apiFetch(staffUrl),
          apiFetch(csUrl),
        ]);
        const sJson = await sRes.json().catch(() => null);
        const cJson = await cRes.json().catch(() => null);
        if (cancelled) return;
        const staffList = Array.isArray(sJson?.data)
          ? sJson.data.map((i) => ({
              id: i.account_id || i.id,
              name: i.name,
              kind: "staff",
            }))
          : [];
        const clientList = Array.isArray(cJson?.data)
          ? cJson.data.map((i) => ({
              id: i.client_id || i.id,
              name: i.name,
              kind: "client",
            }))
          : [];
        const combined = [...staffList, ...clientList].slice(0, 10);
        setSubjectSuggestions(combined);
        setShowSubjectSuggestions(combined.length > 0);
      } catch (e) {
        console.error("Subject suggestions fetch failed", e);
        setSubjectSuggestions([]);
        setShowSubjectSuggestions(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [subjectQuery]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.recognition_date || String(form.recognition_date).trim() === "") {
      showError("Recognition Date is required");
      return;
    }
    const quantityNum = Number(form.quantity);
    const amountNum = Number(form.amount);
    if (!Number.isFinite(quantityNum) || quantityNum <= 0) {
      showError("Quantity must be greater than 0");
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum === 0) {
      showError("Rate cannot be zero");
      return;
    }
    // Prevent client-side save when week is closed
    if (weekClosed) {
      try {
        if (typeof showError === "function")
          showError("Week is closed; adjustments cannot be edited");
        else alert("Week is closed; adjustments cannot be edited");
      } catch (e) {
        console.warn("showError failed", e);
      }
      return;
    }
    try {
      setIsSaving(true);
      const payload = {
        subject_name: form.subject_name,
        subject_type: form.subject_type,
        client_id: form.client_id || null,
        account_id: form.account_id || null,
        recognition_date: form.recognition_date,
        description: form.description,
        note: form.note,
        quantity: quantityNum,
        amount: amountNum,
        add_hst: form.add_hst === "yes" ? 1 : 0,
      };
      const q = forceSave ? "?force=true" : "";
      const res = await apiFetch(
        `/api/accounting/adjustments/${adjustment.adjustment_id}${q}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        let txt = await res.text().catch(() => "");
        try {
          const j = JSON.parse(txt);
          if (j && (j.message || j.error)) txt = j.message || j.error;
        } catch (_) {}
        if (res.status === 403) {
          const msg =
            txt ||
            "Forbidden: week may be closed; adjustments cannot be edited.";
          try {
            if (typeof showError === "function") showError(msg);
            else alert(msg);
          } catch (e) {
            console.warn("showError failed", e);
          }
          setIsSaving(false);

          return;
        }
        const msg = txt || `Save failed ${res.status}`;
        try {
          if (typeof showError === "function") showError(msg);
          else alert(msg);
        } catch (e) {
          console.warn("showError failed", e);
        }
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
      try {
        const j = await res.json().catch(() => null);
        console.log(
          "EditAdjustmentModal: calling showSuccess for saved adjustment",
        );
        if (typeof showSuccess === "function") {
          showSuccess("Adjustment saved");
        } else {
          console.warn("showSuccess not available");
        }
        onSaved && onSaved(j?.data || null);
      } catch (e) {
        console.warn("Error parsing save response", e);
        onSaved && onSaved(null);
      }
      onClose && onClose();
    } catch (e) {
      console.error("Failed to save adjustment", e);
      const msg = e?.message || "Failed to save adjustment";
      try {
        if (typeof showError === "function") showError(msg);
        else alert(msg);
      } catch (er) {
        console.warn("showError failed", er);
      }
      setIsSaving(false);
    }
  };

  if (!isOpen || !adjustment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-xl shadow-lg w-[min(96vw,980px)] p-6 relative flex flex-col ${
          tab === "Audits" ? "h-[82vh]" : "max-h-[82vh]"
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#DC1F00] p-2 rounded-full"
        >
          ✖
        </button>
        <h2 className="text-xl text-[#3A4149] font-semibold mb-2">
          Edit Adjustment
        </h2>
        <div className="h-[1px] bg-[#d5d5d5] mb-4" />
        {!weekClosed ? null : (
          <div className="mb-3 p-3 bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800">
            Week is closed, modifications are disabled.
          </div>
        )}

        <div className="flex gap-2 mb-3">
          <div className="flex border-b border-border mb-4">
            {["General", "Audits"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`pb-2 px-4 text-sm font-medium transition-colors ${
                  tab === t
                    ? "text-[#DC1F00] border-b-2 border-[#DC1F00]"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {t === "General" ? "General Information" : "Audits"}
              </button>
            ))}
          </div>
        </div>

        {tab === "General" && (
          <div className="overflow-y-auto pr-1">
            <form onSubmit={handleSave} className="space-y-3">
            {/* Inline save indicators removed; keep only the top week-closed banner */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-3">
                <div style={{ position: "relative" }}>
                  <label className="block text-sm">
                    Subject (Client or Staff)*
                  </label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={subjectQuery}
                    disabled={weekClosed}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSubjectQuery(v);
                      handleChange(
                        "subject_name",
                        v.replace(/^(Client|Field Staff):\s*/, ""),
                      );
                      handleChange("client_id", null);
                      handleChange("account_id", null);
                      handleChange("subject_type", "");
                    }}
                    required
                    onFocus={() => {
                      if (subjectSuggestions.length)
                        setShowSubjectSuggestions(true);
                    }}
                  />
                  {showSubjectSuggestions && (
                    <div className="absolute z-40 left-0 right-0 bg-white border rounded mt-1 shadow max-h-48 overflow-y-auto">
                      {subjectSuggestions.map((s) => (
                        <div
                          key={`${s.kind}-${s.id}`}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                          onMouseDown={(e) => {
                            e.preventDefault();

                            setSubjectQuery(
                              `${s.kind === "client" ? "Client" : "Field Staff"}: ${s.name}`,
                            );

                            setForm((prev) => ({
                              ...prev,
                              subject_name: s.name,
                              subject_type:
                                s.kind === "client" ? "client" : "fieldstaff",
                              client_id: s.kind === "client" ? s.id : null,
                              account_id: s.kind === "staff" ? s.id : null,
                            }));

                            setShowSubjectSuggestions(false);
                          }}
                        >
                          <div className="text-sm">{s.name}</div>
                          <div className="text-xs text-gray-500 px-2 py-0.5 rounded bg-gray-100">
                            {s.kind}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* type is displayed inside the input value; remove separate label */}
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm">Recognition Date*</label>
                    <DateInput
                      className="w-full border rounded px-2 py-1"
                      value={form.recognition_date ?? ""}
                      disabled={weekClosed}
                      required
                      onChange={(value) =>
                        handleChange("recognition_date", value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm">Line Item Description</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={form.description ?? ""}
                    disabled={weekClosed}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm">Note</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={form.note ?? ""}
                    disabled={weekClosed}
                    onChange={(e) => handleChange("note", e.target.value)}
                  />
                </div>
              </div>

              <div className="col-span-1 space-y-3">
                <div>
                  <label className="block text-sm">Quantity*</label>
                  <input
                    type="number"
                    className="w-full border rounded px-2 py-1"
                    value={form.quantity ?? ""}
                    disabled={weekClosed}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm">Rate*</label>
                  <input
                    type="number"
                    className="w-full border rounded px-2 py-1"
                    value={form.amount ?? ""}
                    disabled={weekClosed}
                    onChange={(e) => handleChange("amount", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm">Total</label>
                  <div className="w-full border rounded px-2 py-1">
                    ${(Number(form.quantity) || 0) * (Number(form.amount) || 0)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm">Add HST</label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="addHst"
                        disabled={weekClosed}
                        checked={(form.add_hst ?? "no") === "no"}
                        onChange={() => handleChange("add_hst", "no")}
                      />{" "}
                      No
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="addHst"
                        disabled={weekClosed}
                        checked={(form.add_hst ?? "no") === "yes"}
                        onChange={() => handleChange("add_hst", "yes")}
                      />{" "}
                      Yes
                    </label>
                  </div>
                </div>

                <div className="mt-3 p-3 rounded border border-gray-200 bg-gray-50">
                  <div className="text-sm font-semibold text-gray-800 mb-1">
                    Summary
                  </div>
                  <div className="text-xs text-gray-600">
                    The amount of{" "}
                    <span className="font-semibold text-gray-900">
                      ${((Number(form.quantity) || 0) * (Number(form.amount) || 0)).toFixed(2)}
                    </span>{" "}
                    will be added to the{" "}
                    {form.subject_type === "client" || form.client_id
                      ? "client's payout"
                      : "staff's payout"}
                    .
                  </div>
                </div>
              </div>
            </div>

              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border rounded px-3 py-2"
                >
                  Close
                </button>
                {!weekClosed && (
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 rounded px-3 py-2 bg-buttonRed text-white"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {tab === "Audits" && (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            <RichAuditsPanel
              subjectId={
                adjustment?.adjustment_id ||
                adjustment?.id ||
                adjustment?.subjectObjectId ||
                null
              }
              fetchUrl={
                adjustment?.adjustment_id
                  ? `/api/accounting/adjustments/${adjustment.adjustment_id}/audits/rich`
                  : ""
              }
              title="Audit History"
              messageMode
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditAdjustmentModal;
